import { type IntlShape } from "react-intl";
import { z } from "zod";

import { NETWORK_PORT_MAX, NETWORK_PORT_MIN } from "./utils/constants";

const getMachineRequestBaseObjectSchema = (intl: IntlShape) =>
	z.object({
		alias: z
			.string(intl.formatMessage({ id: "Alias is required", defaultMessage: "Alias is required" }))
			.min(1, intl.formatMessage({ id: "Alias is required", defaultMessage: "Alias is required" }))
			.max(
				100,
				intl.formatMessage({
					id: "Alias must be at most 100 characters",
					defaultMessage: "Alias must be at most 100 characters",
				}),
			),
		description: z
			.string(
				intl.formatMessage({
					id: "Description needs to be a string",
					defaultMessage: "Description needs to be a string",
				}),
			)
			.optional(),
		ipOrDnsName: z
			.string(intl.formatMessage({ id: "IP or DNS name is required", defaultMessage: "IP or DNS name is required" }))
			.min(1, intl.formatMessage({ id: "IP or DNS name is required", defaultMessage: "IP or DNS name is required" }))
			.max(
				100,
				intl.formatMessage({
					id: "IP or DNS name must be at most 100 characters",
					defaultMessage: "IP or DNS name must be at most 100 characters",
				}),
			)
			.regex(
				/^[a-zA-Z0-9.-]+$/,
				intl.formatMessage({
					id: "Invalid IP Address or Hostname",
					defaultMessage: "Invalid IP Address or Hostname",
				}),
			),
		port: z
			.number(intl.formatMessage({ id: "Port is required", defaultMessage: "Port is required" }))
			.min(
				NETWORK_PORT_MIN,
				intl.formatMessage(
					{ id: "Port must be at least {minimum}", defaultMessage: "Port must be at least {minimum}" },
					{ minimum: NETWORK_PORT_MIN },
				),
			)
			.max(
				NETWORK_PORT_MAX,
				intl.formatMessage(
					{ id: "Port must be at most {maximum}", defaultMessage: "Port must be at most {maximum}" },
					{ maximum: NETWORK_PORT_MAX },
				),
			),
	});

export const getMachineRequestBaseSchema = (intl: IntlShape, existingMachines: MachineRequestBase[] = []) => {
	const schema = getMachineRequestBaseObjectSchema(intl);

	return schema.extend({
		alias: schema.shape.alias.refine(
			(alias) => existingMachines.every((machine) => machine.alias.trim().toLowerCase() !== alias.trim().toLowerCase()),
			intl.formatMessage({
				id: "A machine with this name already exists",
				defaultMessage: "A machine with this name already exists",
			}),
		),
	});
};

export type MachineRequestBase = z.infer<ReturnType<typeof getMachineRequestBaseObjectSchema>>;
